/**
 * `useAgencySettings` — per-agency scoped-settings data layer.
 *
 * Owns: batch-fetch on agency change, per-setting state, upsert,
 * remove. No view concerns, no modal refs, no dirty-broadcast splicing. Just a
 * reactive data contract any view or modal can bind to.
 *
 * Two-piece shape:
 *   - `provideAgencySettings(agencyIdRef, settingNames)` — called once
 *     at the view level. Creates the reactive state + auto-fetches on
 *     agencyId change, then provides the API into the Vue tree.
 *   - `useAgencySettings()` — called by any descendant (modals, item
 *     cards) to read state + mutate. Throws if no ancestor provided.
 *
 * Provides/injects via the published `agencySettingsKey` so the
 * administration UI resolves the data layer across the package boundary.
 */

import {
  inject,
  onScopeDispose,
  provide,
  reactive,
  ref,
  watch,
  type Ref
} from 'vue'
import type {
  ScopedSetting,
  SettingName,
  TypedSettingValues
} from '@gat-ship/types'
import {
  agencySettingsKey,
  type AgencySettingsService
} from '@gat-ship/types/injection-keys'
import { useSettingsApiCalls } from './useSettingsApiCalls'
import { useSettingsSignalREvents } from '@gat-ship/signalr'

/**
 * Install the agency-settings data layer in the Vue tree. Call once
 * from the view that owns the settings page.
 *
 * `settingNames` is optional. When provided, all listed settings are
 * batch-fetched on every agency change — a single HTTP round-trip.
 * When omitted, descendants (per-setting SFCs) are expected to manage
 * their own fetch lifecycle via `refetch([name])` + a watch on
 * `agencyId`. The empty-names mode trades one HTTP-per-setting for
 * fully self-contained setting SFCs; batch-fetch can be re-introduced
 * at the composable level later without touching consumers.
 */
export function provideAgencySettings(
  agencyId: Ref<string | undefined>,
  settingNames: readonly SettingName[] = []
): AgencySettingsService {
  const {
    getSettingsForScopeRequest,
    upsertSettingRequest,
    removeSettingRequest
  } = useSettingsApiCalls()

  // Reactive map keyed by SettingName. Built as a plain reactive so
  // template `scopedSettings[name]` tracks dependencies naturally.
  const scopedSettings = reactive(
    Object.fromEntries(settingNames.map((name) => [name, undefined])) as Record<
      SettingName,
      ScopedSetting<SettingName> | undefined
    >
  )
  const isLoading = ref(false)

  async function fetchBatch(names: readonly SettingName[]) {
    const currentAgencyId = agencyId.value
    if (!currentAgencyId || names.length === 0) return
    try {
      isLoading.value = true
      const result = await getSettingsForScopeRequest({
        nameQueries: names.map((name) => ({ name, version: 1 })),
        scopeLevel: 'agency',
        agencyNodeId: currentAgencyId
      })
      for (const name of names) {
        scopedSettings[name] = result.scopedSettings[
          name
        ] as ScopedSetting<SettingName>
      }
    } finally {
      isLoading.value = false
    }
  }

  async function refetch(names?: readonly SettingName[]) {
    return fetchBatch(names ?? settingNames)
  }

  async function upsert<TName extends SettingName>(
    settingName: TName,
    value: TypedSettingValues[TName],
    settingVersion = 1
  ) {
    const currentAgencyId = agencyId.value
    if (!currentAgencyId) {
      throw new Error('Cannot upsert: no agency selected')
    }
    await upsertSettingRequest({
      settingName,
      scopeLevel: 'agency',
      settingVersion,
      value,
      agencyNodeId: currentAgencyId
    })
    await fetchBatch([settingName])
  }

  async function remove(settingName: SettingName) {
    const currentAgencyId = agencyId.value
    if (!currentAgencyId) {
      throw new Error('Cannot remove: no agency selected')
    }
    await removeSettingRequest({
      settingName,
      scopeLevel: 'agency',
      agencyNodeId: currentAgencyId
    })
    await fetchBatch([settingName])
  }

  function get(settingName: SettingName) {
    return scopedSettings[settingName]
  }

  // Auto-fetch on agency change. `immediate: true` covers the initial
  // mount so the view doesn't have to call refetch explicitly.
  watch(
    agencyId,
    (id) => {
      if (id) void fetchBatch(settingNames)
    },
    { immediate: true }
  )

  // Cross-tab refresh: when another tab/device assigns or removes an
  // agency override, refetch that setting so the UI here stays in sync.
  // Our own-tab mutations already refetch inline in `upsert`/`remove`,
  // and `subscribeToEvent` filters self-originated events by default.
  // Only refetch settings already in the local map — a setting no SFC
  // on this page has registered isn't worth a fetch round-trip.
  function handleCrossTabChange(
    scopeLevel: 'global' | 'agency' | 'user',
    eventAgencyNodeId: string | undefined,
    rawName: string
  ) {
    if (scopeLevel !== 'agency') return
    if (!eventAgencyNodeId) return
    if (eventAgencyNodeId !== agencyId.value) return
    const name = rawName as SettingName
    if (!(name in scopedSettings)) return
    void fetchBatch([name])
  }

  const { subscribeToSettingsEvents, unsubscribeFromSettingsEvents } =
    useSettingsSignalREvents({
      onSettingRemoved: ({ message }) => {
        handleCrossTabChange(
          message.scopeLevel,
          message.agencyNodeId,
          message.name
        )
      },
      onSettingSet: ({ message }) => {
        handleCrossTabChange(
          message.scopeLevel,
          message.agencyNodeId,
          message.name
        )
      }
    })
  subscribeToSettingsEvents()
  onScopeDispose(unsubscribeFromSettingsEvents)

  const api: AgencySettingsService = {
    agencyId,
    scopedSettings,
    isLoading,
    refetch,
    upsert,
    remove,
    get
  }

  provide(agencySettingsKey, api)
  return api
}

/**
 * Consume the agency-settings API from any descendant. Throws if no
 * ancestor called `provideAgencySettings()`.
 */
export function useAgencySettings(): AgencySettingsService {
  const api = inject(agencySettingsKey)
  if (!api) {
    throw new Error(
      'useAgencySettings() must be used within a provideAgencySettings() ancestor'
    )
  }
  return api
}
